import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form, Button, Badge, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import {
    FiCheckCircle,
    FiEye,
    FiMail,
    FiMapPin,
    FiPhone,
    FiSearch,
    FiShoppingBag,
    FiSlash,
    FiX,
} from "react-icons/fi";
import Swal from "sweetalert2";

const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    return `${day} ${month} ${date.getFullYear()}`;
};

const STATUS_OPTIONS = [
    { value: "Pending", label: "Pending", variant: "warning" },
    { value: "Approve", label: "Approved", variant: "success" },
    { value: "Reject", label: "Rejected", variant: "danger" },
];

const statusMeta = (status) => STATUS_OPTIONS.find((option) => option.value === status) || STATUS_OPTIONS[0];

const mediaUrl = (path) => {
    if (!path) return null;
    return String(path).startsWith("http") ? path : `${process.env.REACT_APP_IMG_URL}${path}`;
};

const emptyFilters = { status: "Pending", search: "", pet_type: "", availability: "", per_page: 10 };

const StatusBadge = ({ status }) => {
    const meta = statusMeta(status);
    return <Badge bg={meta.variant}>{meta.label}</Badge>;
};

// Full listing detail with every photo, the video and the seller's contact details.
const DetailModal = ({ show, onClose, sellPet, onStatusChange, updatingId }) => {
    const { t } = useContext(TranslatorContext);

    if (!sellPet) return null;

    const images = [
        ...(sellPet.main_image ? [{ id: "main", image: sellPet.main_image }] : []),
        ...(sellPet.media || []).filter((item) => item.image),
    ];
    const videos = (sellPet.media || []).filter((item) => item.video);

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton className="p-3">
                <Modal.Title className="h5 mb-0">
                    <FiShoppingBag className="me-2" />
                    {sellPet.pet?.name || t("Pet listing")} <StatusBadge status={sellPet.status} />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col md={6}>
                        {images.length > 0 ? (
                            <div className="d-flex flex-wrap gap-2">
                                {images.map((item, index) => (
                                    <a key={item.id || index} href={mediaUrl(item.image)} target="_blank" rel="noreferrer">
                                        <img
                                            src={mediaUrl(item.image)}
                                            alt={sellPet.pet?.name || "Pet"}
                                            style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8, border: "1px solid #e9ecef" }}
                                        />
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted small">{t("No photos uploaded")}</div>
                        )}

                        {videos.length > 0 && (
                            <div className="mt-3">
                                {videos.map((item, index) => (
                                    <video key={item.id || index} src={mediaUrl(item.video)} controls style={{ width: "100%", borderRadius: 8 }} />
                                ))}
                            </div>
                        )}
                    </Col>

                    <Col md={6}>
                        <table className="table table-sm mb-0">
                            <tbody>
                                <tr><td className="text-muted">{t("Pet type")}</td><td className="fw-bold">{sellPet.pet?.type || "—"}</td></tr>
                                <tr><td className="text-muted">{t("Breed")}</td><td className="fw-bold">{sellPet.pet?.breed || "—"}</td></tr>
                                <tr><td className="text-muted">{t("Gender")}</td><td className="fw-bold">{sellPet.pet?.gender || "—"}</td></tr>
                                <tr><td className="text-muted">{t("Age")}</td><td className="fw-bold">{sellPet.pet?.age ? `${sellPet.pet.age} months` : "—"}</td></tr>
                                <tr><td className="text-muted">{t("Colour")}</td><td className="fw-bold">{sellPet.pet?.color || "—"}</td></tr>
                                <tr><td className="text-muted">{t("Price")}</td><td className="fw-bold text-success">Rs {Number(sellPet.price || 0).toLocaleString()}</td></tr>
                                <tr><td className="text-muted">{t("Price type")}</td><td className="fw-bold">{sellPet.selling_style || "—"}</td></tr>
                                <tr><td className="text-muted">{t("Vaccination")}</td><td className="fw-bold">{sellPet.health_record_status || "—"}</td></tr>
                                <tr><td className="text-muted">{t("Availability")}</td><td className="fw-bold">{sellPet.available ? t("Available") : t("Sold")}</td></tr>
                                <tr><td className="text-muted">{t("Submitted")}</td><td className="fw-bold">{formatDate(sellPet.created_at)}</td></tr>
                            </tbody>
                        </table>
                    </Col>

                    <Col md={12}>
                        <h6 className="mb-1">{t("Seller")}</h6>
                        <div className="small text-muted">
                            <div>{sellPet.seller?.name || "—"}</div>
                            <div><FiMail className="me-1" size={12} />{sellPet.seller?.email || "—"}</div>
                            <div><FiPhone className="me-1" size={12} />{sellPet.phone || sellPet.seller?.phone || "—"}</div>
                            <div><FiMapPin className="me-1" size={12} />{sellPet.location || "—"}</div>
                        </div>
                    </Col>

                    {sellPet.health_record_history && (
                        <Col md={12}>
                            <h6 className="mb-1">{t("Medical notes")}</h6>
                            <p className="small text-muted mb-0">{sellPet.health_record_history}</p>
                        </Col>
                    )}

                    {sellPet.description && (
                        <Col md={12}>
                            <h6 className="mb-1">{t("Description")}</h6>
                            <p className="small text-muted mb-0" style={{ whiteSpace: "pre-line" }}>{sellPet.description}</p>
                        </Col>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer className="p-2">
                <Button variant="secondary" onClick={onClose}>{t("Close")}</Button>
                {sellPet.status !== "Reject" && (
                    <Button variant="danger" disabled={updatingId === sellPet.id} onClick={() => onStatusChange(sellPet, "Reject")}>
                        <FiSlash className="me-1" /> {t("Reject")}
                    </Button>
                )}
                {sellPet.status !== "Approve" && (
                    <Button variant="success" disabled={updatingId === sellPet.id} onClick={() => onStatusChange(sellPet, "Approve")}>
                        <FiCheckCircle className="me-1" /> {t("Approve")}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default function SellPetIndexPage() {
    const { t } = useContext(TranslatorContext);
    const AuthToken = localStorage.getItem("authToken");

    const [sellPets, setSellPets] = useState([]);
    const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
    const [petTypes, setPetTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showDetail, setShowDetail] = useState(false);

    const [tempFilters, setTempFilters] = useState(emptyFilters);
    const [activeFilters, setActiveFilters] = useState(emptyFilters);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0 });

    useEffect(() => {
        fetchSellPets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters, pagination.current_page]);

    const fetchSellPets = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.current_page,
                per_page: activeFilters.per_page,
                ...(activeFilters.status && { status: activeFilters.status }),
                ...(activeFilters.search && { search: activeFilters.search }),
                ...(activeFilters.pet_type && { pet_type: activeFilters.pet_type }),
                ...(activeFilters.availability && { availability: activeFilters.availability }),
            });

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/sell-pets?${params}`, {
                headers: { Authorization: "Bearer " + AuthToken },
            });

            const rows = response.data.data || [];
            const responsePagination = response.data.pagination || {};
            const perPage = responsePagination.per_page || activeFilters.per_page;
            const currentPage = responsePagination.current_page || 1;

            setSellPets(rows);
            setCounts(response.data.counts || { all: 0, pending: 0, approved: 0, rejected: 0 });
            setPetTypes(response.data.pet_types || []);
            setPagination({
                current_page: currentPage,
                last_page: responsePagination.last_page || 1,
                per_page: perPage,
                total: responsePagination.total || 0,
                from: rows.length ? (currentPage - 1) * perPage + 1 : 0,
                to: rows.length ? (currentPage - 1) * perPage + rows.length : 0,
            });
        } catch (error) {
            console.error("Error fetching pet listings:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Failed to fetch pet listings",
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 2500,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setTempFilters((prev) => ({ ...prev, [name]: value }));
    };

    const applySearch = () => {
        setActiveFilters({ ...tempFilters });
        setPagination((prev) => ({ ...prev, current_page: 1 }));
    };

    const clearFilters = () => {
        const cleared = { ...emptyFilters, status: "" };
        setTempFilters(cleared);
        setActiveFilters(cleared);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
    };

    const selectStatusTab = (status) => {
        setTempFilters((prev) => ({ ...prev, status }));
        setActiveFilters((prev) => ({ ...prev, status }));
        setPagination((prev) => ({ ...prev, current_page: 1 }));
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setPagination((prev) => ({ ...prev, current_page: page }));
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const changeStatus = async (sellPet, status) => {
        const isReject = status === "Reject";
        const confirmed = await Swal.fire({
            icon: isReject ? "warning" : "question",
            title: isReject ? t("Reject this listing?") : t("Approve this listing?"),
            text: isReject
                ? t("The seller's pet will not appear on the website.")
                : t("The pet will appear in the website marketplace right away."),
            showCancelButton: true,
            confirmButtonText: isReject ? t("Yes, reject") : t("Yes, approve"),
            confirmButtonColor: isReject ? "#dc3545" : "#198754",
            cancelButtonText: t("Cancel"),
        });

        if (!confirmed.isConfirmed) return;

        setUpdatingId(sellPet.id);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/sell-pets/status/${sellPet.id}`,
                { status },
                { headers: { Authorization: "Bearer " + AuthToken } },
            );

            const updated = response.data?.data;
            setSellPets((prev) => prev.map((item) => (item.id === sellPet.id ? { ...item, ...updated } : item)));
            setSelected((prev) => (prev && prev.id === sellPet.id ? { ...prev, ...updated } : prev));
            setCounts((prev) => recountAfterChange(prev, sellPet.status, status));

            Swal.fire({
                icon: "success",
                title: "Success",
                text: isReject ? "Listing rejected" : "Listing approved",
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 2000,
            });

            // Keep the active tab honest: a row that no longer matches it should disappear.
            if (activeFilters.status && activeFilters.status !== status) fetchSellPets();
        } catch (error) {
            console.error("Error updating listing status:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Failed to update the listing",
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 2500,
            });
        } finally {
            setUpdatingId(null);
        }
    };

    const recountAfterChange = (current, fromStatus, toStatus) => {
        const key = { Pending: "pending", Approve: "approved", Reject: "rejected" };
        const next = { ...current };
        if (key[fromStatus]) next[key[fromStatus]] = Math.max(0, (next[key[fromStatus]] || 0) - 1);
        if (key[toStatus]) next[key[toStatus]] = (next[key[toStatus]] || 0) + 1;
        return next;
    };

    const openDetail = (sellPet) => {
        setSelected(sellPet);
        setShowDetail(true);
    };

    const statusTabs = [
        { value: "", label: t("All"), count: counts.all },
        { value: "Pending", label: t("Pending"), count: counts.pending },
        { value: "Approve", label: t("Approved"), count: counts.approved },
        { value: "Reject", label: t("Rejected"), count: counts.rejected },
    ];

    const renderBootstrapPagination = () => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        const endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        items.push(<Pagination.Prev key="prev" onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page === 1} />);

        for (let page = startPage; page <= endPage; page += 1) {
            items.push(
                <Pagination.Item key={page} active={page === pagination.current_page} onClick={() => handlePageChange(page)}>
                    {page}
                </Pagination.Item>,
            );
        }

        items.push(<Pagination.Next key="next" onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page} />);

        return items;
    };

    return (
        <PageLayout>
            <div className="sell-pet-index-page">
                <div className="sticky-header p-3 bg-white shadow-sm mb-4 rounded">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <h2 className="mb-0 fw-bold">
                                <FiShoppingBag className="me-2 text-primary" />
                                {t("Pet Listing Approvals")}
                            </h2>
                            <p className="text-muted mt-2 mb-0">
                                {t("Review pets submitted from the website. Only approved listings are shown for sale.")}
                            </p>
                        </Col>
                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                            <Badge bg="warning" className="fs-6">{counts.pending} {t("awaiting review")}</Badge>
                        </Col>
                    </Row>
                </div>

                <Card className="shadow-sm mb-4">
                    <Card.Body>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {statusTabs.map((tab) => (
                                <Button
                                    key={tab.value || "all"}
                                    size="sm"
                                    variant={activeFilters.status === tab.value ? "primary" : "outline-primary"}
                                    onClick={() => selectStatusTab(tab.value)}
                                >
                                    {tab.label} ({tab.count || 0})
                                </Button>
                            ))}
                        </div>

                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Search")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        size="sm"
                                        placeholder="Pet name, seller, phone or location..."
                                        value={tempFilters.search}
                                        onChange={handleFilterChange}
                                        onKeyDown={(event) => event.key === "Enter" && applySearch()}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Pet type")}</Form.Label>
                                    <Form.Select name="pet_type" size="sm" value={tempFilters.pet_type} onChange={handleFilterChange}>
                                        <option value="">{t("All types")}</option>
                                        {petTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Availability")}</Form.Label>
                                    <Form.Select name="availability" size="sm" value={tempFilters.availability} onChange={handleFilterChange}>
                                        <option value="">{t("All")}</option>
                                        <option value="available">{t("Available")}</option>
                                        <option value="sold">{t("Sold")}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mt-1">
                                <Form.Label className="small fw-bold d-block">&nbsp;</Form.Label>
                                <div className="d-flex gap-2">
                                    <Button variant="primary" size="sm" onClick={applySearch}>
                                        <FiSearch className="me-1" /> {t("Search")}
                                    </Button>
                                    <Button variant="danger" size="sm" onClick={clearFilters}>
                                        <FiX className="me-1" /> {t("Clear All")}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <Card className="shadow-sm">
                            <Card.Body className="p-0">
                                <div className="table-responsive-wrapper">
                                    <Table responsive hover className="table mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "8%" }}>{t("Photo")}</th>
                                                <th style={{ width: "20%" }}>{t("Pet")}</th>
                                                <th style={{ width: "20%" }}>{t("Seller")}</th>
                                                <th style={{ width: "12%" }}>{t("Price")}</th>
                                                <th style={{ width: "12%" }}>{t("Location")}</th>
                                                <th style={{ width: "10%" }}>{t("Submitted")}</th>
                                                <th style={{ width: "8%" }}>{t("Status")}</th>
                                                <th style={{ width: "10%" }} className="text-center">{t("Actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sellPets.length > 0 ? (
                                                sellPets.map((sellPet) => (
                                                    <tr key={sellPet.id}>
                                                        <td>
                                                            {sellPet.main_image ? (
                                                                <img
                                                                    src={mediaUrl(sellPet.main_image)}
                                                                    alt={sellPet.pet?.name || "Pet"}
                                                                    style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 6 }}
                                                                />
                                                            ) : (
                                                                <div className="bg-light rounded" style={{ width: 46, height: 46 }} />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <strong>{sellPet.pet?.name || "—"}</strong>
                                                            <div className="small text-muted">
                                                                {[sellPet.pet?.type, sellPet.pet?.breed, sellPet.pet?.gender].filter(Boolean).join(" · ") || "—"}
                                                            </div>
                                                            {!sellPet.available && <Badge bg="secondary" className="mt-1">{t("Sold")}</Badge>}
                                                        </td>
                                                        <td>
                                                            <div>{sellPet.seller?.name || "—"}</div>
                                                            <div className="small text-muted">
                                                                <FiPhone className="me-1" size={12} />{sellPet.phone || sellPet.seller?.phone || "—"}
                                                            </div>
                                                        </td>
                                                        <td className="fw-bold text-success">Rs {Number(sellPet.price || 0).toLocaleString()}</td>
                                                        <td className="small">{sellPet.location || "—"}</td>
                                                        <td className="small">{formatDate(sellPet.created_at)}</td>
                                                        <td><StatusBadge status={sellPet.status} /></td>
                                                        <td className="text-center">
                                                            <div className="d-flex gap-1 justify-content-center">
                                                                <Button variant="info" size="sm" title={t("View Details")} onClick={() => openDetail(sellPet)}>
                                                                    <FiEye size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    title={t("Approve")}
                                                                    disabled={updatingId === sellPet.id || sellPet.status === "Approve"}
                                                                    onClick={() => changeStatus(sellPet, "Approve")}
                                                                >
                                                                    <FiCheckCircle size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    title={t("Reject")}
                                                                    disabled={updatingId === sellPet.id || sellPet.status === "Reject"}
                                                                    onClick={() => changeStatus(sellPet, "Reject")}
                                                                >
                                                                    <FiSlash size={14} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <FiShoppingBag size={48} className="text-muted mb-2" />
                                                            <p className="text-muted mb-0">{t("No pet listings found")}</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>

                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("entries")}
                                </div>
                                <Pagination className="mb-0">{renderBootstrapPagination()}</Pagination>
                            </div>
                        )}
                    </>
                )}
            </div>

            <DetailModal
                show={showDetail}
                onClose={() => setShowDetail(false)}
                sellPet={selected}
                onStatusChange={changeStatus}
                updatingId={updatingId}
            />
        </PageLayout>
    );
}
